import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ApiError } from '../api/http'

const toApiError = (e: unknown) =>
  e instanceof ApiError ? e : new ApiError({ status: 0, code: 'unknown', detail: e instanceof Error ? e.message : String(e) })

/** Busy flag + ApiError capture for a form or an action, with localised field errors. */
export function useForm() {
  const { t, te } = useI18n()
  const busy = ref(false)
  const error = ref<ApiError | null>(null)

  async function submit(fn: () => Promise<unknown>): Promise<boolean> {
    busy.value = true
    error.value = null
    try {
      await fn()
      return true
    } catch (e) {
      error.value = toApiError(e)
      return false
    } finally {
      busy.value = false
    }
  }

  /** Localised message for a field (dotted path), or '' when the field is valid. */
  function fieldErr(field: string): string {
    const fe = error.value?.fieldErrors[field]
    if (!fe) return ''
    return fe.code && te(`fieldErrors.${fe.code}`) ? t(`fieldErrors.${fe.code}`) : fe.message
  }

  return { busy, error, submit, fieldErr }
}

/** Drop empty-string fields so optional inputs left blank are omitted from the request. */
export function compact<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '')) as T
}

/** Localised top-level message for an ApiError. */
export function useErrorText() {
  const { t, te } = useI18n()
  return (e: ApiError) => (te(`errors.${e.code}`) ? t(`errors.${e.code}`) : e.detail || t('errors.unknown'))
}
